import { useCallback, useEffect, useState } from 'react';
import { Button } from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import type { LabQuizView } from '@/lib/types';
import { QuizEditor } from './QuizEditor';
import { QuizResponses } from './QuizResponses';

const STATUS_LABELS = { draft: 'Draft', open: 'Open', closed: 'Closed' } as const;

export function QuizzesPanel({ projectId }: { projectId: string }) {
  const [quizzes, setQuizzes] = useState<LabQuizView[] | null>(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setQuizzes(await labsApi.listQuizzes(projectId));
      setError('');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = quizzes?.find((quiz) => quiz.id === selectedId) ?? null;
  const editing = creating || selected;

  if (error && !quizzes) return <p className="text-sm text-destructive" role="alert">{error}</p>;
  if (!quizzes) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      {!editing && (
        <>
          <div className="flex min-h-9 items-center justify-between gap-4">
            <h2 className="text-sm font-semibold">Quizzes</h2>
            <Button type="button" onClick={() => setCreating(true)}>New quiz</Button>
          </div>
          {quizzes.length === 0 && <p className="text-sm text-muted-foreground">No quizzes yet.</p>}
          {quizzes.length > 0 && (
            <ul className="divide-y rounded-lg border">
              {quizzes.map((quiz) => (
                <li key={quiz.id}>
                  <button
                    type="button"
                    className="grid w-full gap-2 px-4 py-3 text-left sm:grid-cols-[minmax(0,1fr)_7rem_6rem] sm:items-center"
                    onClick={() => setSelectedId(quiz.id)}
                  >
                    <span className="truncate font-medium">{quiz.title}</span>
                    <span className="text-sm text-muted-foreground">{STATUS_LABELS[quiz.status]}</span>
                    <span className="text-sm text-muted-foreground sm:text-right">
                      {quiz.totalResponses} {quiz.totalResponses === 1 ? 'response' : 'responses'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {editing && (
        <>
          <QuizEditor
            projectId={projectId}
            quiz={selected}
            onSaved={(quiz) => {
              setCreating(false);
              setSelectedId(quiz.id);
              setQuizzes((current) => {
                const rest = (current ?? []).filter((item) => item.id !== quiz.id);
                return [quiz, ...rest];
              });
            }}
            onDeleted={() => {
              setSelectedId(null);
              setCreating(false);
              void load();
            }}
            onCancel={() => {
              setCreating(false);
              setSelectedId(null);
            }}
          />
          {selected?.currentVersion && (
            <QuizResponses
              quizId={selected.id}
              versions={selected.versions}
              currentVersionId={selected.currentVersion.id}
            />
          )}
        </>
      )}
    </div>
  );
}
